/* eslint-disable react/jsx-no-constructed-context-values */

import { Anchor } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	useSuspenseApiGetAllowedAuthMethods,
	useSuspenseApiPublicEnv,
} from '@repo/api-codegen';
import { useMount } from 'ahooks';
import axios from 'axios';
import type React from 'react';
import { useCallback, useLayoutEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api } from '../../../network';
import { AuthStepStartContext } from './AuthStepStartContext';
import type { SsoTypes } from './utils';

export interface AuthStepStartContextProviderProps {
	children: React.ReactNode;
	initialForceSSO?: SsoTypes | null;
}

export function AuthStepStartContextProvider({
	children,
	initialForceSSO,
}: AuthStepStartContextProviderProps) {
	const [searchParams] = useSearchParams();
	const [forceSSO, setForceSSO] = useState<SsoTypes | null>(
		initialForceSSO || null
	);
	const [email, setEmail] = useState<string>('');
	const [error, setError] = useState<string | null>(searchParams.get('error'));
	const [showTrialPrompt, setShowTrialPrompt] = useState(false);

	const { data: publicEnv } = useSuspenseApiPublicEnv({});
	const navigate = useNavigate();

	const allowedAuthMethodsResponse = useSuspenseApiGetAllowedAuthMethods({});
	const supportedMethods = allowedAuthMethodsResponse.data.methods;

	const resetSSO = useCallback(() => {
		setForceSSO(null);
	}, []);

	useMount(() => {
		// This request serves two purposes:
		// 1. It will set the CSRF cookie for the user.
		// 2. It will set the URL for the instance, used for SAML.
		axios.post(`${api()}auth/u/url/`, {
			value: window.location.href,
		});
	});

	const handleTrialPrompt = useCallback(() => {
		setShowTrialPrompt(true);
		showNotification({
			title: 'Speak to the Secoda team',
			autoClose: false,
			message: (
				<Anchor href="https://secoda.co/start-your-trial">
					Start a free trial
				</Anchor>
			),
		});
		setTimeout(() => {
			window.open('https://secoda.co/start-your-trial', '_blank');
		}, 2500);
	}, []);

	// eslint-disable-next-line no-use-effect/no-use-effect
	useLayoutEffect(() => {
		const searchError = searchParams.get('error');
		// Search error is a technical error and does not contain trial upgrade
		// link, so do not show upgrade prompt.
		if (searchError) {
			showNotification({
				message: searchError ?? '',
			});
		} else if (
			error?.includes('https://secoda.co/start-your-trial') &&
			!showTrialPrompt
		) {
			handleTrialPrompt();
		}
	}, [searchParams, showTrialPrompt, error, handleTrialPrompt]);

	const startEmailAuth = useCallback(async () => {
		if (publicEnv?.IS_ON_PREMISE || publicEnv?.IS_MANAGED) {
			// Skip the global login flow and go directly to the verify step
			navigate(`/auth/verify?email=${encodeURIComponent(email)}`);
		} else {
			const { origin } = window.location;
			const authUrl = `https://auth.secoda.co/auth/email?origin=${encodeURIComponent(origin)}&email=${encodeURIComponent(email)}&region=${encodeURIComponent(publicEnv?.REGION || '')}`;
			window.location.href = authUrl;
		}
	}, [
		email,
		navigate,
		publicEnv?.IS_MANAGED,
		publicEnv?.IS_ON_PREMISE,
		publicEnv?.REGION,
	]);

	const startSamlAuth = useCallback(
		async (domain: string) => {
			if (publicEnv?.IS_ON_PREMISE || publicEnv?.IS_MANAGED) {
				// Skip the global login flow and go directly to the verify step
				const escapedDomain = encodeURIComponent(domain).replaceAll(/\./g, '+');
				const { origin } = window.location;
				window.location.href = `${origin}/api/v1/auth/saml/${escapedDomain}/sp-initiate/`;
			} else {
				const { origin } = window.location;
				const authUrl = `https://auth.secoda.co/auth/saml?origin=${encodeURIComponent(origin)}&domain=${encodeURIComponent(domain)}&region=${encodeURIComponent(publicEnv?.REGION || '')}`;
				window.location.href = authUrl;
			}
		},
		[publicEnv?.IS_MANAGED, publicEnv?.IS_ON_PREMISE, publicEnv?.REGION]
	);

	return (
		<AuthStepStartContext.Provider
			value={{
				forceSSO,
				supportedMethods,
				resetSSO,
				startEmailAuth,
				email,
				setEmail,
				startSamlAuth,
				error,
				setError,
			}}
		>
			{children}
		</AuthStepStartContext.Provider>
	);
}
